// src/features/pdf/components/PdfViewerModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Box, Paper, IconButton, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Core viewer
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';

// Plugins
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

// Define the type for the file prop, it can be a File object or a Uint8Array with a name
export type ViewerFile = File | { name: string; data: Uint8Array };

interface PdfViewerModalProps {
    open: boolean;
    onClose: () => void;
    file: ViewerFile | null;
}

const modalStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const paperStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '90vw',
    height: '90vh',
    maxWidth: '1200px',
    maxHeight: '900px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    overflow: 'hidden', // Important for child layout
};

const headerStyle = {
    p: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 1,
    borderColor: 'divider',
    flexShrink: 0,
};

const viewerContainerStyle = {
    flexGrow: 1,
    overflowY: 'auto' as 'auto', // Important for viewer scrolling
    position: 'relative',
};

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ open, onClose, file }) => {
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>('');

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    useEffect(() => {
        // This effect creates a blob URL from the file data when the file prop changes.
        // It's necessary because the Viewer component works best with URLs.
        if (file) {
            let blob;
            if (file instanceof File) {
                // If it's a File object, use it directly
                blob = new Blob([file], { type: file.type });
                setFileName(file.name);
            } else {
                // If it's our custom OutputFile object { name, data }, create a blob
                blob = new Blob([file.data], { type: 'application/pdf' });
                setFileName(file.name);
            }
            const url = URL.createObjectURL(blob);
            setFileUrl(url);

            // Cleanup function to revoke the object URL to avoid memory leaks
            return () => {
                URL.revokeObjectURL(url);
                setFileUrl(null);
                setFileName('');
            };
        }
    }, [file]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="pdf-viewer-modal-title"
            aria-describedby="pdf-viewer-modal-description"
            sx={modalStyle}
        >
            <Paper sx={paperStyle}>
                <Box sx={headerStyle}>
                    <Typography id="pdf-viewer-modal-title" variant="h6" component="h2" noWrap sx={{ maxWidth: 'calc(100% - 48px)' }}>
                        {fileName || 'PDF Preview'}
                    </Typography>
                    <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={viewerContainerStyle}>
                    {fileUrl ? (
                        // Using the PDF.js worker from a CDN for simplicity.
                        // For production, you might want to host it locally.
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                            <Viewer
                                fileUrl={fileUrl}
                                plugins={[defaultLayoutPluginInstance]}
                                defaultScale={SpecialZoomLevel.PageFit}
                            />
                        </Worker>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export default PdfViewerModal;